import { useCallback, useEffect, useState } from "react";
import SubTabs from "@/components/SubTabs";
import { Loader2, Inbox, ShoppingCart, PackageCheck, Factory, Pencil, Layers, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { opsApi } from "./lib/api";
import GroupOrders from "./GroupOrders";
import ReviewSection from "./ReviewSection";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2,
  maximumFractionDigits: 2 })}`;
const SECTIONS = [["inbox", "Inbox", Inbox], ["pos", "Purchase Orders", ShoppingCart],
  ["receiving", "Receiving", PackageCheck], ["manufacturers", "Manufacturers", Factory],
  ["groups", "Group Orders", Layers], ["review", "Review", Search]];

/** OPS-5 — the buyer's own page, across every job: what needs chasing, every purchase order,
 *  what has landed, and the manufacturer master that holds the procurement contacts. */
export default function PurchasingPage() {
  const [section, setSection] = useState("inbox");
  const [data, setData] = useState(null);
  const [manus, setManus] = useState(null);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setData(await opsApi.purchasingOverview()); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load purchasing"); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (section === "manufacturers" && !manus) {
      opsApi.manufacturers().then(setManus).catch(() => setManus({ manufacturers: [] }));
    }
  }, [section, manus]);

  const saveManufacturer = async () => {
    setBusy(true);
    try {
      await opsApi.manufacturerPatch(edit.id, {
        contact_name: edit.contact_name || "", contact_email: edit.contact_email || "",
        contact_phone: edit.contact_phone || "",
        lead_time_days: Number(edit.lead_time_days) || 0,
        ship_via: edit.ship_via || "", freight_default: edit.freight_default || "prepaid",
        notes: edit.notes || "",
      });
      toast.success(`${edit.name} saved`);
      setEdit(null);
      setManus(await opsApi.manufacturers());
    } catch (e) { toast.error(e?.response?.data?.detail || "That could not be saved"); }
    finally { setBusy(false); }
  };

  if (!data) {
    return <div className="flex items-center justify-center gap-2 py-20 text-sm text-slate-500">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading purchasing…
    </div>;
  }
  const s = data.summary;

  return (
    <div className="flex-1 min-h-0 flex flex-col" data-testid="ops-purchasing-page">
      <SubTabs
        tabs={SECTIONS.map(([key, label, icon]) => ({ key, label, icon,
                                                      testId: `purchasing-section-${key}` }))}
        active={section}
        onChange={setSection}
        testId="purchasing-sections"
        divider={false}
      />
      <div className="page-body space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[["Needs attention", `${s.needs_attention} PO(s)`, "purchasing-attention"],
          ["Awaiting acknowledgement", `${s.awaiting_ack} PO(s)`, "purchasing-awaiting"],
          ["Committed", money(s.committed), "purchasing-committed"],
          ["Received to date", money(s.received_value), "purchasing-received"]]
          .map(([label, value, tid]) => (
            <div key={label} className="rounded-lg border border-slate-200 bg-white p-4"
                 data-testid={tid}>
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</p>
              <p className="stat-num text-2xl font-semibold text-slate-900">{value}</p>
            </div>
          ))}
      </div>

      {section === "inbox" && (
        <div className="space-y-2" data-testid="purchasing-inbox">
          {data.inbox.length === 0 && (
            <p className="rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-600">
              Nothing needs chasing. Late acknowledgements, ship dates that have moved and
              order-by dates that have passed land here.
            </p>
          )}
          {data.inbox.map((po) => (
            <div key={po.id} className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"
                 data-testid={`purchasing-inbox-${po.po_number}`}>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-mono font-semibold">{po.po_number}</span>
                <span className="text-slate-700">{po.manufacturer}</span>
                <span className="text-xs text-slate-500">
                  {po.job_number} — {po.project_name}
                </span>
                <span className="ml-auto font-semibold">{money(po.total)}</span>
              </div>
              <ul className="mt-1 space-y-0.5 text-xs text-amber-900">
                {po.reasons.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          ))}
        </div>
      )}

      {section === "pos" && (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white"
             data-testid="purchasing-pos">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">PO</th>
                <th className="px-3 py-2 text-left">Job</th>
                <th className="px-3 py-2 text-left">Manufacturer</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Ordered</th>
                <th className="px-3 py-2 text-left">Ship</th>
                <th className="px-3 py-2 text-right">Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.purchase_orders.map((po) => (
                <tr key={po.id} data-testid={`purchasing-po-${po.po_number}`}>
                  <td className="px-3 py-1.5 font-mono">{po.po_number}</td>
                  <td className="px-3 py-1.5 text-xs">{po.job_number} — {po.project_name}</td>
                  <td className="px-3 py-1.5">{po.manufacturer}</td>
                  <td className="px-3 py-1.5">{po.status_label}</td>
                  <td className="px-3 py-1.5 text-xs">{(po.ordered_at || "").slice(0, 10) || "—"}</td>
                  <td className="px-3 py-1.5 text-xs">
                    {(po.current_ship_date || po.expected_ship_date || "").slice(0, 10) || "—"}
                    {po.ship_slipped && <span className="ml-1 text-amber-700">slipped</span>}
                  </td>
                  <td className="px-3 py-1.5 text-right">{money(po.total)}</td>
                </tr>
              ))}
              {data.purchase_orders.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500">
                  No purchase orders yet.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {section === "receiving" && (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white"
             data-testid="purchasing-receiving">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Received</th>
                <th className="px-3 py-2 text-left">Job</th>
                <th className="px-3 py-2 text-left">PO</th>
                <th className="px-3 py-2 text-left">Part</th>
                <th className="px-3 py-2 text-right">Qty</th>
                <th className="px-3 py-2 text-right">Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.receipts.map((r) => (
                <tr key={r.id}>
                  <td className="px-3 py-1.5 text-xs">{(r.received_at || "").slice(0, 10)}</td>
                  <td className="px-3 py-1.5 text-xs">{r.job_number}</td>
                  <td className="px-3 py-1.5 font-mono text-xs">{r.po_number}</td>
                  <td className="px-3 py-1.5">{r.item_number || r.description}</td>
                  <td className="px-3 py-1.5 text-right">{Number(r.qty).toLocaleString()}</td>
                  <td className="px-3 py-1.5 text-right">{money(r.amount)}</td>
                </tr>
              ))}
              {data.receipts.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-500">
                  Nothing received yet.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {section === "manufacturers" && (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white"
             data-testid="purchasing-manufacturers">
          {!manus ? (
            <div className="flex items-center gap-2 px-4 py-8 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading manufacturers…
            </div>
          ) : (
            <>
              <p className="border-b border-slate-100 bg-slate-50 px-4 py-2 text-xs text-slate-500">
                {manus.note}
              </p>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Manufacturer</th>
                    <th className="px-3 py-2 text-left">Procurement contact</th>
                    <th className="px-3 py-2 text-right">Lead time</th>
                    <th className="px-3 py-2 text-left">Ship via</th>
                    <th className="px-3 py-2 text-left">Freight</th>
                    <th className="px-3 py-2 text-right">Lines seen</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {manus.manufacturers.map((m) => (
                    <tr key={m.id} data-testid={`purchasing-manufacturer-${m.key}`}>
                      <td className="px-3 py-1.5">
                        {m.name}
                        <span className="ml-1 font-mono text-[10px] text-slate-400">
                          {m.short_code}
                        </span>
                      </td>
                      <td className={`px-3 py-1.5 text-xs ${m.contact_ready ? ""
                        : "text-rose-700"}`}>
                        {m.contact_email
                          ? `${m.contact_name || ""} ${m.contact_email}`.trim()
                          : "no contact — purchasing is blocked"}
                      </td>
                      <td className="px-3 py-1.5 text-right">{m.lead_time_days || "—"}</td>
                      <td className="px-3 py-1.5 text-xs">{m.ship_via || "—"}</td>
                      <td className="px-3 py-1.5 text-xs">{m.freight_default}</td>
                      <td className="px-3 py-1.5 text-right text-xs text-slate-500">
                        {m.lines_seen}
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        {manus.can_edit && (
                          <button onClick={() => setEdit({ ...m })}
                                  data-testid={`purchasing-edit-${m.key}`}
                                  className="inline-flex items-center gap-1 rounded-md border border-emerald-600 bg-transparent text-emerald-700 hover:bg-emerald-50 dark:border-emerald-500 dark:text-emerald-400 dark:hover:bg-emerald-950/40 px-2 py-0.5 text-xs font-medium">
                            <Pencil className="h-3 w-3" /> Edit
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}

      {section === "groups" && <GroupOrders />}
      {section === "review" && <ReviewSection canEdit={data.can_edit} />}

      {edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
             onClick={() => setEdit(null)}>
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl"
               onClick={(e) => e.stopPropagation()} data-testid="purchasing-manufacturer-dialog">
            <h3 className="text-base font-semibold text-slate-900">{edit.name}</h3>
            <p className="mt-1 text-xs text-slate-500">
              The procurement contact is who the purchase order goes to. Lead time drives the
              order-by date.
            </p>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {[["contact_name", "Contact name", "text"], ["contact_email", "Contact email", "email"],
                ["contact_phone", "Phone", "text"], ["lead_time_days", "Lead time (days)", "number"],
                ["ship_via", "Ship via", "text"]].map(([k, label, type]) => (
                <label key={k} className="block text-xs">
                  <span className="text-slate-500">{label}</span>
                  <Input type={type} value={edit[k] ?? ""}
                         onChange={(e) => setEdit((m) => ({ ...m, [k]: e.target.value }))}
                         className="mt-1 h-8 text-xs" data-testid={`purchasing-field-${k}`} />
                </label>
              ))}
              <label className="block text-xs">
                <span className="text-slate-500">Freight default</span>
                <select value={edit.freight_default || "prepaid"}
                        onChange={(e) => setEdit((m) => ({ ...m, freight_default: e.target.value }))}
                        data-testid="purchasing-field-freight"
                        className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-xs">
                  <option value="prepaid">Prepaid</option>
                  <option value="amount">Billed as an amount</option>
                </select>
              </label>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setEdit(null)}
                      data-testid="purchasing-manufacturer-cancel">Cancel</Button>
              <Button size="sm" onClick={saveManufacturer} disabled={busy}
                      data-testid="purchasing-manufacturer-save">
                {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />} Save
              </Button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
